import { useMemo, useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Loading, Empty, ErrorState } from "../components/states";
import {
  Button,
  StatusPill,
  Money,
  Chips,
  Chevron,
  columnsFor,
  plural,
  shortDate,
  SectionHeading,
  SubHeading,
  Tile,
  monthLabel,
  ModelledFootnote,
} from "../components/kit";
import { useViewer } from "../lib/viewer";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Button as UiButton } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { formatCents } from "@shared/money";
/* The month boundary, and the month-to-date rollup, live on the Statements
   screen — see the note on `monthToDate` there. Both billing screens read the
   same `invoices.list` payload, so both have to answer "invoiced this month"
   with the same number. */
import { monthToDate, utcMonthKey } from "./Statements";

/**
 * Invoices.
 *
 * A clinic manager sees what they are charged. Only the owner sees cost, list
 * price, the applied rule and the margin — and that is enforced on the server,
 * so those fields are simply absent from a manager's payload rather than hidden
 * by this component.
 *
 * For the owner, every line shows **which rule priced it, in words**. "Why is
 * this line $386.63?" has to be answerable live, on screen, in front of Dee.
 *
 * Two layouts, one row component. At 390px an invoice is a card — identifier
 * and clinic on the left, money right-aligned on the right. At `lg` the same
 * rows become a real table, because seventeen invoices scanned for "which one
 * is late and how much is left" is a columns problem, not a cards problem.
 *
 * **Margin is never green.** It used to be, and it sat two lines under a green
 * "Paid" pill: two greens, one meaning "this clinic has settled" and the other
 * meaning "we made money", indistinguishable at a glance across a projector.
 * Green belongs to the status language. Margin is a neutral figure in a neutral
 * column, and the *presence* of the column is what says "owner".
 */

type Filter = "all" | "unpaid" | "overdue" | "paid";

/**
 * The table geometry, written out in full rather than assembled.
 *
 * Tailwind reads class strings literally, so a template built from a variable
 * produces no CSS at all. Four whole strings, all four visible to the scanner.
 *
 * The two axes are the two columns that are not always there. A clinic manager
 * sees one clinic, so a Clinic column repeating that clinic's name seventeen
 * times is a column of noise — it goes, and the flexible track moves onto the
 * invoice number. Margin follows the payload, not the role.
 */
const COLUMNS = {
  clinicMargin:
    "lg:grid-cols-[8rem_minmax(0,1fr)_4rem_5rem_5.5rem_6rem_6rem_5.5rem_1.25rem]",
  clinic: "lg:grid-cols-[8rem_minmax(0,1fr)_4rem_5rem_5.5rem_6rem_6rem_1.25rem]",
  margin:
    "lg:grid-cols-[minmax(8rem,1fr)_4rem_5rem_5.5rem_6rem_6rem_5.5rem_1.25rem]",
  plain: "lg:grid-cols-[minmax(8rem,1fr)_4rem_5rem_5.5rem_6rem_6rem_1.25rem]",
};

/**
 * The order Dee needs, not the order the database hands back.
 *
 * The server sorts newest-issued-first, which is right for an audit log and
 * wrong for a debt list: it puts the invoice that is eighty-nine days late at
 * the bottom of a scroll. Overdue first and most-overdue-first inside that,
 * then unpaid-but-not-yet-due by due date, then everything settled, newest
 * first. Client-side only — nothing about what the server was asked changes.
 */
const chaseRank = (invoice: { status: string; outstandingCents: number }): number =>
  invoice.status === "overdue" ? 0 : invoice.outstandingCents > 0 ? 1 : 2;

/**
 * The chips, applied on the client.
 *
 * These are exactly `invoices.list`'s own predicates: `status` compares against
 * the derived status the server already put on the payload, and `unpaidOnly` is
 * its `isOutstanding`. Filtering here rather than asking again is what keeps the
 * screen on **one** subscription — see the note in the component.
 */
const matchesFilter = (
  invoice: { status: string; outstandingCents: number },
  filter: Filter,
): boolean => {
  if (filter === "unpaid") return invoice.outstandingCents > 0;
  if (filter === "overdue" || filter === "paid") return invoice.status === filter;
  return true;
};

export function Invoices() {
  const viewer = useViewer();
  const [filter, setFilter] = useState<Filter>("all");
  /* One subscription, unfiltered, and the chips narrow it here.
   *
   * The headline figures and the chip counts have to come from every invoice,
   * never from the rows currently on screen — a header that said "$0 overdue"
   * because the Paid chip was selected would be the same class of bug as the
   * approvals count that used to disagree with the rows under it. That used to
   * mean two `invoices.list` subscriptions on one screen, one of them a subset
   * of the other. Both were capped at the server's `limit = 100`, so the subset
   * is derivable exactly. */
  const allInvoices = useQuery(api.invoices.list, {});
  const [openId, setOpenId] = useState<Id<"invoices"> | null>(null);

  /* Every headline figure on this screen, in one pass over the payload.
   *
   * All six come from *every* invoice rather than the rows on screen, so none
   * of them depends on the chip — which is why they memoize on `allInvoices`
   * alone and survive both a filter tap and a row expand untouched. They used
   * to be seven separate walks of the same array, redone on each of those.
   *
   * "What have we spent this month?" is answered here rather than left to be
   * added up by eye off the rows. `issuedAt` and `totalCents` are already on
   * every row of the payload this screen holds, for every role that may see an
   * invoice at all, so no extra query and no extra field. */
  const totals = useMemo(() => {
    const invoices = allInvoices ?? [];
    let outstanding = 0;
    let overdueCents = 0;
    let overdueCount = 0;
    let unpaidCount = 0;
    for (const invoice of invoices) {
      outstanding += invoice.outstandingCents;
      // Same predicate the Overdue chip sends to the server, so the count on
      // the chip and the rows behind it cannot disagree.
      if (invoice.status === "overdue") {
        overdueCount += 1;
        overdueCents += invoice.outstandingCents;
      }
      if (invoice.outstandingCents > 0) unpaidCount += 1;
    }
    const thisMonth = utcMonthKey(Date.now());
    const month = monthToDate(invoices, thisMonth);
    return {
      outstanding,
      overdueCents,
      overdueCount,
      unpaidCount,
      thisMonth,
      monthToDateCents: month.spendCents,
      monthToDateCount: month.invoiceCount,
    };
  }, [allInvoices]);

  if (allInvoices === undefined) {
    return <Loading rows={5} label="Loading invoices" />;
  }

  const {
    outstanding,
    overdueCents,
    overdueCount,
    unpaidCount,
    thisMonth,
    monthToDateCents,
    monthToDateCount,
  } = totals;

  const invoices = allInvoices.filter((invoice) => matchesFilter(invoice, filter));
  const isOwner = viewer.role === "owner";

  // Derived from what the server actually sent, not from the role: the margin
  // column exists because there are margins in the payload. A clinic role gets
  // `null` here and therefore gets no column, with nothing for this component
  // to get wrong.
  const showMargin = invoices.some((invoice) => invoice.marginCents !== null);
  // A manager is scoped to one clinic; naming it on every row is a column of
  // the same word.
  const showClinic = !viewer.clinicName;
  const columns = columnsFor(COLUMNS, showClinic, showMargin);

  const rows = [...invoices].sort((a, b) => {
    const rankA = chaseRank(a);
    const rankB = chaseRank(b);
    if (rankA !== rankB) return rankA - rankB;
    if (rankA === 2) return b.issuedAt - a.issuedAt;
    return a.dueAt - b.dueAt;
  });

  return (
    <div className="space-y-4 pb-8">
      <SectionHeading
        title="Invoices"
        subtitle={`${viewer.clinicName ?? "All clinics"} · ${allInvoices.length} ${plural(
          allInvoices.length,
          "invoice",
        )}`}
      />

      {/* Two figures, in the order they get asked for: what is still owed, and
          what this month has cost so far. The second is the question the
          manager opened this screen with — she was adding rows up by eye. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Tile
          size="hero"
          label={overdueCount > 0 ? "Outstanding — some of it late" : "Outstanding"}
          value={formatCents(outstanding)}
          tone={overdueCount > 0 ? "red" : "neutral"}
          hint={
            overdueCount > 0
              ? `${overdueCount} ${plural(overdueCount, "invoice")} overdue · ${formatCents(
                  overdueCents,
                )}`
              : "Nothing is past its due date."
          }
        />
        <Tile
          label={`Invoiced in ${monthLabel(thisMonth)}`}
          value={formatCents(monthToDateCents)}
          hint={`${monthToDateCount} ${plural(monthToDateCount, "invoice")} issued so far this month`}
        />
      </div>

      <Chips<Filter>
        value={filter}
        onChange={setFilter}
        options={[
          { value: "all", label: "All", count: allInvoices.length },
          { value: "unpaid", label: "Unpaid", count: unpaidCount },
          { value: "overdue", label: "Overdue", count: overdueCount },
          { value: "paid", label: "Paid", count: allInvoices.length - unpaidCount },
        ]}
      />

      {invoices.length === 0 ? (
        <Empty
          title="No invoices here"
          body={
            filter === "all"
              ? "Invoices are raised automatically when a clinic confirms a delivery."
              : "Nothing matching that filter."
          }
        />
      ) : (
        <div className="space-y-2">
          {/* The column header exists only where the columns do. */}
          <div
            className={cn(
              "hidden px-4 text-xs font-medium tracking-wide text-muted-foreground uppercase",
              columns,
            )}
          >
            <span>Invoice</span>
            {showClinic ? <span>Clinic</span> : null}
            <span>Issued</span>
            <span>Due</span>
            <span>Status</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Balance</span>
            {showMargin ? <span className="text-right">Margin</span> : null}
            <span />
          </div>

          {rows.map((invoice) =>
            openId === invoice._id ? (
              <InvoiceDetail
                key={invoice._id}
                invoiceId={invoice._id}
                isOwner={isOwner}
                onClose={() => setOpenId(null)}
              />
            ) : (
              /* A raw <button> carrying stock card tokens, not a registry
                 `Button`. The typography argument that used to be here is
                 void — `text-sm` is the target now and this row sets it
                 itself. What remains is layout: the registry base is
                 `inline-flex justify-center whitespace-nowrap` with its own
                 height and padding, and this element is a full-width card that
                 becomes a nine-column grid at `lg`. Every one of those would
                 have to be undone.

                 The look is the registry's regardless: `border` + `bg-card` +
                 `rounded-xl` is exactly what `<Card>` draws, and `bg-muted/50`
                 is the hover a stock `<TableRow>` uses — which is what these
                 rows are at `lg`. */
              <button
                key={invoice._id}
                type="button"
                onClick={() => setOpenId(invoice._id)}
                className="group block w-full rounded-xl border bg-card text-left text-sm shadow-sm transition-colors hover:bg-muted/50"
              >
                {/* 390px: a card.
                    Balance, total and margin are three different shapes, not
                    three grey lines distinguished by a trailing word. Balance
                    is the figure Dee reconciles, so it is the big near-black
                    number; the total sits under it as context; margin gets a
                    labelled row of its own below a hairline. */}
                <span className="flex items-center gap-3 p-4 lg:hidden">
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold whitespace-nowrap tabular-nums">
                        {invoice.number}
                      </span>
                      <StatusPill status={invoice.status} kind="invoice" />
                      {/* Muted, not red. The Overdue pill is the red on this
                          row; a second red saying the same thing twice leaves
                          the row with no hierarchy at all. */}
                      {invoice.daysOverdue > 0 ? (
                        <span className="text-xs text-muted-foreground">
                          {invoice.daysOverdue} {plural(invoice.daysOverdue, "day")} late
                        </span>
                      ) : null}
                    </span>
                    <span className="mt-0.5 block truncate text-muted-foreground">
                      {showClinic ? `${invoice.clinicName} · ` : ""}
                      {invoice.lineCount} {plural(invoice.lineCount, "line")} · issued{" "}
                      {shortDate(invoice.issuedAt)} · due {shortDate(invoice.dueAt)}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    {invoice.outstandingCents > 0 ? (
                      <>
                        <span className="block text-base font-semibold tabular-nums">
                          <Money cents={invoice.outstandingCents} />
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          of <Money cents={invoice.totalCents} />
                        </span>
                      </>
                    ) : (
                      <span className="block text-base font-semibold tabular-nums">
                        <Money cents={invoice.totalCents} />
                      </span>
                    )}
                    {invoice.marginCents !== null ? (
                      <span className="mt-1 block border-t pt-1 text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                        margin <Money cents={invoice.marginCents} />
                      </span>
                    ) : null}
                  </span>
                  <Chevron />
                </span>

                {/* lg: the same row, as a table row. */}
                <span className={cn("hidden px-4 py-3", columns)}>
                  <span className="min-w-0">
                    <span className="block font-semibold whitespace-nowrap tabular-nums">
                      {invoice.number}
                    </span>
                    <span className="block text-[11px] text-muted-foreground">
                      {invoice.lineCount} {plural(invoice.lineCount, "line")}
                    </span>
                  </span>
                  {showClinic ? (
                    <span className="truncate">{invoice.clinicName}</span>
                  ) : null}
                  <span className="text-muted-foreground tabular-nums">
                    {shortDate(invoice.issuedAt)}
                  </span>
                  <span className="tabular-nums">
                    <span className="block text-muted-foreground">
                      {shortDate(invoice.dueAt)}
                    </span>
                    {invoice.daysOverdue > 0 ? (
                      <span className="block text-[11px] text-muted-foreground">
                        {invoice.daysOverdue} {plural(invoice.daysOverdue, "day")} late
                      </span>
                    ) : null}
                  </span>
                  <span>
                    <StatusPill status={invoice.status} kind="invoice" />
                  </span>
                  {/* Amount is context. Balance is the number being chased, so
                      it carries the weight the total used to. */}
                  <span className="text-right text-muted-foreground tabular-nums">
                    <Money cents={invoice.totalCents} />
                  </span>
                  <span className="text-right font-semibold tabular-nums">
                    {invoice.outstandingCents > 0 ? (
                      <Money cents={invoice.outstandingCents} />
                    ) : (
                      <span className="font-normal text-muted-foreground">—</span>
                    )}
                  </span>
                  {showMargin ? (
                    <span className="text-right text-muted-foreground tabular-nums">
                      {invoice.marginCents !== null ? (
                        <Money cents={invoice.marginCents} />
                      ) : (
                        <span>—</span>
                      )}
                    </span>
                  ) : null}
                  <Chevron />
                </span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function InvoiceDetail({
  invoiceId,
  isOwner,
  onClose,
}: {
  invoiceId: Id<"invoices">;
  isOwner: boolean;
  onClose: () => void;
}) {
  const invoice = useQuery(api.invoices.get, { invoiceId });
  const costBasis = useQuery(api.pricing.costBasisSummary, isOwner ? {} : "skip");
  const pdf = useQuery(api.invoices.pdfUrl, { invoiceId });
  const recordPayment = useMutation(api.invoices.recordPayment);
  const { busy, run } = useBusyAction();

  if (invoice === undefined) return <Loading rows={4} label="Loading invoice" />;
  /* `null` is "gone, or not yours". Rendering nothing made the row the reader
     had just tapped disappear with no explanation — a designed state costs one
     card. */
  if (invoice === null) {
    return (
      <ErrorState
        title="That invoice is no longer available"
        body="It may have been deleted, or it belongs to another clinic."
        onRetry={onClose}
      />
    );
  }

  const settle = () =>
    run(
      () =>
        recordPayment({
          invoiceId,
          amountCents: invoice.outstandingCents,
          method: "EFT",
        }),
      {
        failed: "Could not record payment",
        done: (result) => ({
          happened: "Payment recorded",
          next: `${invoice.number} is now ${result.status}.`,
        }),
      },
    );

  return (
    /* The registry card, accent on the border: the expanded invoice is the one
       open thing in a list of closed ones, and `--primary` is how this app says
       that now. `block gap-0 p-4` is the shim every card in this app applies —
       the registry card is built around `CardHeader`/`CardContent` slots this
       one does not use, and its children lay themselves out. */
    <Card className="block gap-0 border-primary p-4 lg:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold tabular-nums">
            {invoice.number}
          </div>
          <div className="text-sm text-muted-foreground">
            {invoice.clinicName} · issued {shortDate(invoice.issuedAt)} · due{" "}
            {shortDate(invoice.dueAt)}
          </div>
          {invoice.dispatchNumber ? (
            <div className="text-xs text-muted-foreground">
              from {invoice.dispatchNumber}
            </div>
          ) : null}
        </div>
        <Button variant="outline" className="shrink-0" onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusPill status={invoice.status} kind="invoice" />
        {/* The same file the clinic was emailed, not a re-render. Server-gated:
            `invoices:pdfUrl` runs the identical clinic check as reading the
            invoice, because a storage URL is a bearer token. */}
        {invoice.hasPdf && pdf ? (
          /* `asChild` is the registry's own answer to "a link that looks like a
             button": the anchor keeps `href`, `download` and `target`, and the
             styling is stock `outline` with nothing added. */
          <UiButton asChild variant="outline">
            <a
              href={pdf.url}
              target="_blank"
              rel="noreferrer"
              download={pdf.filename}
            >
              PDF
            </a>
          </UiButton>
        ) : null}
      </div>

      {/* Unmissable, and next to the margin rather than in a footnote. */}
      {isOwner && invoice.costBasisIsModelled && costBasis ? (
        <div className="mt-3">
          <ModelledFootnote
            modelled={costBasis.modelled}
            total={costBasis.total}
            bandMin={costBasis.bandMin}
            bandMax={costBasis.bandMax}
          />
        </div>
      ) : null}

      {/*
        A real table, because that is what it is: quantity, item, line total.
        Stock rows, stock borders, stock hover — the overrides that used to
        argue these rows are not pressable are gone, and the registry's own
        row treatment is what a reader of any other shadcn table expects.

        Two classes survive on the cells, both layout rather than style:

          `whitespace-normal` — the base is `whitespace-nowrap`, right for a
            data grid and wrong for the one cell holding a 60-character item
            name at 390px. Only the item column gets it; the money columns keep
            nowrap so they never wrap mid-figure.
          `align-baseline` — the item cell is two or three blocks tall, so the
            base's `align-middle` would float the money against the middle of
            the pricing note instead of the line it belongs to.

        `w-full` on the item cell is the standard auto-layout trick for "this
        column absorbs the slack".
      */}
      <Table className="mt-2">
        <TableCaption className="sr-only">
          The {invoice.lines.length} {plural(invoice.lines.length, "line")} on{" "}
          {invoice.number}
        </TableCaption>
        <TableBody>
          {invoice.lines.map((line: any) => (
            <TableRow key={line._id}>
              <TableCell className="num align-baseline font-semibold">
                ×{line.qty}
              </TableCell>
              <TableCell className="w-full align-baseline whitespace-normal">
                <div className="font-medium">{line.itemName}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  <span>
                    <Money cents={line.clinicUnitPriceCents} /> each
                  </span>
                  {/* Owner only — the server omits these fields for everyone
                      else. Neutral, not green: green on this screen means
                      "settled". */}
                  {line.listPriceCents !== undefined ? (
                    <>
                      <span>· list <Money cents={line.listPriceCents} /></span>
                      <span>· cost <Money cents={line.nexoraCostCents} /></span>
                      <span className="font-semibold text-foreground">
                        · margin <Money cents={line.marginCents} />
                      </span>
                    </>
                  ) : null}
                </div>
                {/* SPEC: "why is this line $78?" must be answerable live, on
                    screen. `appliedRuleLabel` comes off the line the server
                    priced — owner only, from `redactInvoiceLine` — and it is
                    labelled rather than left as an unexplained grey sentence.

                    Muted, not teal. It used to be an accent-tinted strip, and
                    teal in this app means "press me" — an explanation of a
                    price is the least pressable thing on the screen. Same
                    treatment as the identical strip on Pricing. */}
                {line.appliedRuleLabel ? (
                  <div className="mt-1 rounded-md border-l-2 bg-muted px-2 py-1 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">Priced by</span>{" "}
                    {line.appliedRuleLabel}
                  </div>
                ) : null}
              </TableCell>
              <TableCell className="num text-right align-baseline font-semibold">
                <Money cents={line.lineTotalCents} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* An invoice's totals belong under its right-hand money column, not
          stretched across a 1000px card. */}
      <dl className="mt-4 space-y-1 border-t pt-3 text-sm lg:ml-auto lg:w-80">
        <div className="flex justify-between">
          <dt className="text-muted-foreground">Subtotal</dt>
          <dd><Money cents={invoice.subtotalCents} /></dd>
        </div>
        {invoice.deliveryFeeCents > 0 ? (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Delivery</dt>
            <dd><Money cents={invoice.deliveryFeeCents} /></dd>
          </div>
        ) : null}
        {invoice.adminFeeCents > 0 ? (
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Admin</dt>
            <dd><Money cents={invoice.adminFeeCents} /></dd>
          </div>
        ) : null}
        <div className="flex justify-between text-base font-semibold">
          <dt>Total</dt>
          <dd><Money cents={invoice.totalCents} /></dd>
        </div>
        {invoice.amountPaidCents > 0 ? (
          <>
            <div className="flex justify-between text-green-700">
              <dt>Paid</dt>
              <dd>−<Money cents={invoice.amountPaidCents} /></dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>Outstanding</dt>
              <dd><Money cents={invoice.outstandingCents} /></dd>
            </div>
          </>
        ) : null}
        {invoice.marginCents !== null ? (
          <div className="mt-1 flex justify-between border-t pt-2 text-muted-foreground">
            <dt className="font-semibold">Nexora margin</dt>
            <dd className="font-semibold tabular-nums"><Money cents={invoice.marginCents} /></dd>
          </div>
        ) : null}
      </dl>

      {invoice.payments.length > 0 ? (
        <div className="mt-3 border-t pt-3 lg:ml-auto lg:w-80">
          <SubHeading>Payments</SubHeading>
          {/* Stock rows. `TableBody` already drops the last row's border, so a
              short payment list ends on the card's own edge rather than on a
              line of its own. */}
          <Table className="mt-1 text-muted-foreground">
            <TableCaption className="sr-only">
              Payments received against {invoice.number}
            </TableCaption>
            <TableBody>
              {invoice.payments.map((payment) => (
                <TableRow key={payment._id}>
                  <TableCell className="w-full">
                    {shortDate(payment.receivedAt)} · {payment.method}
                  </TableCell>
                  <TableCell className="text-right">
                    <Money cents={payment.amountCents} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}

      {isOwner && invoice.outstandingCents > 0 ? (
        <div className="mt-4 lg:ml-auto lg:w-80">
          <Button className="w-full" onClick={settle} disabled={busy}>
            Record payment of <Money cents={invoice.outstandingCents} className="ml-1" />
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
